import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ClientProvider } from "@/lib/clientContext";
import { ChromeContext } from "@/lib/chromeContext";
import { getNote, isNoteDone, isNoteFailed } from "@/lib/noteClaim";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import ExperienceMode from "./ExperienceMode";
import AskRevqDock from "./AskRevqDock";
import NpiBootstrapModal from "./onboarding/NpiBootstrapModal";
import AlertsNudge from "./alerts/AlertsNudge";
import ClientPickerOverlay from "./ClientPickerOverlay";
import LensUploadModal from "./claim/LensUploadModal";
import ClaimWorkspace from "./claim/ClaimWorkspace";
import ChartV2 from "@/pages/ChartV2";
import NoteEditor from "@/components/encounter/NoteEditor";
import AddPatientPage from "@/components/patients/AddPatientPage";
import MultiPartUploadsTab from "@/components/claim/MultiPartUploadsTab";
import EraWorkspaceV2 from "@/pages/EraWorkspaceV2";
import { NewVisitTab } from "@/components/telehealthv2/THNewVisit";
import { VisitScreen } from "@/components/telehealthv2/THVisit";
import { AwayBar } from "@/components/telehealthv2/THAwayBar";
import { LongVisitBanner } from "@/components/telehealthv2/THLongBanner";
import { setSessionOpener, patientName, chartOf } from "@/components/telehealthv2/thModel";
import { useLive } from "@/components/telehealthv2/thLive";
import { api } from "@/lib/api";
import { ChartTabsContext } from "@/lib/chartTabs";
import { X as XIcon, User as UserIcon, ChevronLeft, Pencil as PencilIcon, Plus as PlusIcon, Video as VideoIcon, FileText as DocIcon, Upload as UploadIcon } from "lucide-react";
import { applyTextScale, getTextScale } from "@/lib/textScale";

const PAGE_TITLES = {
  "/home": "Home",
  "/patients": "Patients",
  "/setup": "Setup",
  "/payments": "Payment Posting",
  "/payments/legacy": "Payment Posting",
  "/payments/upload": "Upload Remittance",
  "/payments-v2": "Payment Posting › ERA Builder",
  "/revq": "Ask RevQ",
  "/create-claims": "Claims Queue",
  "/encounter-review": "Encounter review",
  "/dictation": "Dictation",
  "/telemedicine": "Telemedicine",
  "/telehealth-v2": "Telehealth",
  "/lumina-documents": "Lumina Documents",
  "/patient-documents": "Chart Documents",
  "/clinic-documents": "Clinic Documents",
  "/eligibility": "Eligibility",
  "/authorizations": "Authorizations",
  "/denials": "Denials & Rejections",
  "/patient-billing": "Patient Billing",
  "/records/claims": "Claims",
  "/records/encounters": "Encounters",
  "/records/superbills": "Superbills",
  "/records/patients": "Patients",
  "/recommendations": "Recommendations",
  "/analytics": "Analytics",
  "/rules": "Rules",
  "/team": "Team",
  "/modules": "Modules",
  "/attachments": "Attachments",
  "/data-migration": "Data Migration",
  "/settings": "Settings",
};

function titleForPath(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith("/payments/era/")) return "View ERA";
  return null;
}

const TELE_ERR = {
  visit_not_started: "This visit hasn't started yet.", provider_absent: "You're not marked present in this visit. Click Start again.",
  patient_slot_occupied: "The patient is already connected from another device. Use Reset patient connection.", link_invalid: "This visit link is no longer valid.",
  consent_required: "The patient still needs to give consent.", visit_not_found: "This visit couldn't be found. It may have been cancelled.",
};
const teleStartError = (e) => TELE_ERR[e?.response?.headers?.["x-televisit-error-code"]] || (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't start the visit. Try again.");

export default function AppShell() {
  const { token, bootstrapping } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Per-page browser tab title, keyed off the active route.
  useEffect(() => {
    const page = titleForPath(location.pathname);
    document.title = page ? (page.includes("›") ? page : `${page} · revQ`) : "revQ";
  }, [location.pathname]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [lens, setLens] = useState({ open: false, kind: "pdf" });
  const [watch, setWatch] = useState(null);      // { id, filename } — Watch it code / opened from an alert
  const [bgJobs, setBgJobs] = useState([]);       // running-in-background jobs awaiting one completion alert

  // ── Chart / note tabs (app-shell wide) ────────────────────────────────────
  const MAX_TABS = 8;
  const claimName = (t) => t.items?.[t.index]?.name || t.items?.[t.index]?.patient_name || "Claim";
  const claimDos = (t) => { const d = t.items?.[t.index]?.date_of_service; return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : ""; };
  const tabKeys = (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const els = [...e.currentTarget.querySelectorAll(".cts-pagetab,.cts-tabbtn")], i = els.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault(); els[(i + (e.key === "ArrowRight" ? 1 : -1) + els.length) % els.length].focus();
  };
  const live = useLive(), liveRef = useRef(null), chartTabsRef = useRef([]), openVisitRef = useRef(null);
  // Nav folds to icons while a televisit is open (waiting room included); a manual toggle wins for the session.
  const [navManual, setNavManual] = useState(() => { try { const v = sessionStorage.getItem("revq.nav.manual"); return v === "1" ? true : v === "0" ? false : null; } catch { return null; } });
  const navCollapsed = sidebarCollapsed || (navManual ?? !!live);
  const toggleNav = () => { const v = !navCollapsed; setNavManual(v); try { sessionStorage.setItem("revq.nav.manual", v ? "1" : "0"); } catch { /* storage blocked */ } };
  liveRef.current = live;
  const [chartTabs, setChartTabs] = useState(() => { try { return JSON.parse(sessionStorage.getItem("revq.charttabs") || "[]").filter((t) => t.kind !== "visit").map((t) => (t.errand && t.errand.telehealth ? { ...t, errand: null } : t.kind === "claim" ? { ...t, editing: false } : t)); } catch { return []; } });
  chartTabsRef.current = chartTabs;
  openVisitRef.current = (a) => (chartOf(a) ? openTeleChart(a) : openVisit(a));
  useEffect(() => setSessionOpener((a) => openVisitRef.current(a)), []);
  const [activeTab, setActiveTab] = useState("page"); // "page" | tab key
  // One-time slide-in for a newly created tab (ignored on first mount / restore).
  const [newTabKey, setNewTabKey] = useState(null);
  const prevKeysRef = useRef(null);
  useEffect(() => {
    const keys = chartTabs.map(keyOf);
    if (prevKeysRef.current === null) { prevKeysRef.current = keys; return; }
    const added = keys.find((k) => !prevKeysRef.current.includes(k));
    prevKeysRef.current = keys;
    if (added) setNewTabKey(added);
  }, [chartTabs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setChartTabs((cur) => (cur.some((t) => keyOf(t) === activeTab && t.dot) ? cur.map((t) => (keyOf(t) === activeTab ? { ...t, dot: false } : t)) : cur)); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [overflowOpen, setOverflowOpen] = useState(false);
  useEffect(() => { try { sessionStorage.setItem("revq.charttabs", JSON.stringify(chartTabs)); } catch { /* ignore */ } }, [chartTabs]);

  const pageLabel = titleForPath(location.pathname) || "Page";
  const keyOf = (t) => (t.kind === "claim" ? `claim:${t.id}` : t.kind === "note" ? `note:${t.noteId}` : t.kind === "addPatient" ? "addPatient" : t.kind === "newVisit" ? `nv:${t.id}` : t.kind === "visit" ? `visit:${t.id}` : t.kind === "mpu" ? "mpu" : t.uuid);

  const openChart = (p) => {
    const uuid = p?.uuid || p?.chart_uuid || p?.chart_id;
    if (!uuid) { toast("This patient has no linked chart yet."); return; }
    const name = p?.name || p?.full_name || "Patient";
    const errand = p?.errand ? { ...p.errand, n: Date.now() } : null;
    setChartTabs((cur) => {
      if (cur.some((t) => t.kind !== "note" && t.kind !== "claim" && t.uuid === uuid)) return errand || p?.intent === "documents" ? cur.map((t) => (t.kind !== "note" && t.uuid === uuid ? { ...t, ...(errand ? { errand, fromLabel: pageLabel } : { intent: "documents", intentN: Date.now() }) } : t)) : cur;
      if (cur.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return cur; }
      const tab = { kind: "chart", uuid, name, fromLabel: p?.fromLabel || pageLabel, origin: p?.after || null, intent: p?.intent || null, intentN: Date.now(), errand };
      const at = p?.after ? cur.findIndex((t) => keyOf(t) === p.after) : -1;
      return at >= 0 ? [...cur.slice(0, at + 1), tab, ...cur.slice(at + 1)] : [...cur, tab];
    });
    setActiveTab(uuid);
  };
  // One door for "open a chart with something already in progress": {note_id, edit} or {appointment_id, telehealth}.
  const openChartTab = (chartUuid, errand, name) => {
    if (!chartUuid || (errand && "note_id" in errand && !errand.note_id)) { toast("That link is missing the chart or the visit."); return; }
    openChart({ uuid: chartUuid, name, errand });
  };
  const openNote = (p) => {
    const noteId = p?.noteId; if (!noteId) return;
    setChartTabs((cur) => {
      if (cur.some((t) => t.kind === "note" && t.noteId === noteId)) return cur;
      if (cur.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return cur; }
      return [...cur, { kind: "note", noteId, name: p?.name || "Patient", chartUuid: p?.chartUuid || null, fromLabel: p?.fromLabel || pageLabel }];
    });
    setActiveTab(`note:${noteId}`);
  };
  // Part B: Multi-part uploads as a single tab. One at a time; reopening focuses it and swaps the batch.
  const openMpu = (p = {}) => {
    setChartTabs((cur) => {
      const origin = activeTab === "mpu" ? undefined : activeTab;
      if (cur.some((t) => t.kind === "mpu")) return cur.map((t) => (t.kind === "mpu" ? { ...t, batchId: p.batchId ?? null, backLabel: p.backLabel || t.backLabel, origin: origin ?? t.origin } : t));
      if (cur.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return cur; }
      return [...cur, { kind: "mpu", id: "mpu", batchId: p.batchId ?? null, backLabel: p.backLabel || pageLabel, origin: origin || "page" }];
    });
    setTimeout(() => setActiveTab("mpu"), 0);
  };
  const openEra = (fileId, p = {}) => {
    if (!fileId) return;
    const ex = chartTabsRef.current.find((t) => t.kind === "era" && t.fileId === fileId);
    if (ex) { setActiveTab(ex.uuid); return; }
    if (chartTabsRef.current.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return; }
    const uuid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `era-${Date.now()}`;
    setChartTabs((cur) => [...cur, { kind: "era", uuid, fileId, label: p.label || "ERA Builder" }]);
    setTimeout(() => setActiveTab(uuid), 0);
  };
  // Claim tabs: one per open claim; items + index keep the queue order for the pager / Scan next.
  const claimAt = (id, skip) => chartTabsRef.current.find((t) => t.kind === "claim" && keyOf(t) !== skip && t.items[t.index]?.id === id);
  const openClaim = ({ items, index = 0, backLabel, backNav, railTab = null }) => {
    const it = items?.[index]; if (!it) return;
    const ex = claimAt(it.id);
    if (ex) { setActiveTab(keyOf(ex)); toast("Already open — switched to that tab"); return; }
    if (chartTabsRef.current.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return; }
    const id = Date.now(), origin = activeTab;
    setChartTabs((cur) => [...cur, { kind: "claim", id, items, index, origin, backLabel: backLabel || pageLabel, backNav: backNav || null, railTab, editing: false }]);
    setTimeout(() => setActiveTab(`claim:${id}`), 0);
  };
  const claimIndex = (k, ni) => {
    const t = chartTabsRef.current.find((x) => keyOf(x) === k), it = t?.items[ni]; if (!it) return;
    const other = claimAt(it.id, k);
    if (other) { setActiveTab(keyOf(other)); toast("Already open — switched to that tab"); return; }
    setChartTabs((cur) => cur.map((x) => (keyOf(x) === k ? { ...x, index: ni, editing: false } : x)));
  };
  const setClaimEditing = (k, v) => setChartTabs((cur) => (cur.some((x) => keyOf(x) === k && !!x.editing !== !!v) ? cur.map((x) => (keyOf(x) === k ? { ...x, editing: !!v } : x)) : cur));
  const hasTab = (k) => k === "page" || chartTabsRef.current.some((x) => keyOf(x) === k);
  const claimBack = (t) => { if (t.backNav) { setActiveTab("page"); navigate(t.backNav); return; } setActiveTab(t.origin && hasTab(t.origin) ? t.origin : "page"); };
  const openClaimIds = chartTabs.filter((t) => t.kind === "claim").map((t) => t.items[t.index]?.id);
  const [closeAsk, setCloseAsk] = useState(null);
  // Add patient — a single tab that remembers where it was opened from (origin).
  const openAddPatient = (opts) => {
    const origin = activeTab;
    const originLabel = origin === "page" ? pageLabel : (chartTabs.find((t) => keyOf(t) === origin)?.name || "Back");
    setChartTabs((cur) => {
      if (cur.some((t) => t.kind === "addPatient")) {
        return cur.map((t) => (t.kind === "addPatient" ? { ...t, prefill: opts?.prefill || t.prefill, focus: opts?.focus || t.focus } : t));
      }
      if (cur.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return cur; }
      return [...cur, { kind: "addPatient", origin, originLabel, prefill: opts?.prefill || null, focus: opts?.focus || "first" }];
    });
    setActiveTab("addPatient");
  };
  const openNewVisit = (prefill) => {
    const id = Date.now();
    let ok = true;
    setChartTabs((cur) => { if (cur.length >= MAX_TABS) { ok = false; toast("You have 8 tabs open — close one to open another."); return cur; } return [...cur, { kind: "newVisit", id, name: prefill?.patient ? `New visit · ${prefill.patient.full_name}` : "New visit", prefill: prefill || null }]; });
    setTimeout(() => { if (ok) setActiveTab(`nv:${id}`); }, 0);
  };
  const focusVisit = (id) => { setChartTabs((cur) => cur.map((t) => (t.kind === "visit" && t.id === id ? { ...t, hidden: false, dot: false } : t))); setActiveTab(`visit:${id}`); };
  const openVisit = async (a) => {
    const id = a.appointment_id;
    const exists = chartTabsRef.current.some((t) => t.kind === "visit" && t.id === id);
    if (!exists && chartTabsRef.current.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return; }
    try {
      // Start and re-join are the same idempotent /start call: same room, provider marked present.
      const { data } = await api.post(`/api/edi/televisit/appointments/${id}/start`, {});
      const start = { ...data, _n: Date.now() };
      if (exists) { setChartTabs((cur) => cur.map((t) => (t.kind === "visit" && t.id === id ? { ...t, start } : t))); focusVisit(id); window.dispatchEvent(new CustomEvent("revq:th-refresh")); return; }
      setChartTabs((cur) => [...cur, { kind: "visit", id, name: `Visit · ${patientName(a)}`, appt: a, start }]);
      setTimeout(() => setActiveTab(`visit:${id}`), 0);
      window.dispatchEvent(new CustomEvent("revq:th-refresh"));
    } catch (e) { toast.error(teleStartError(e)); }
  };
  // Chart patients: start (idempotent) → chart tab with the telehealth errand → note panel on Telehealth, call starting.
  const openTeleChart = async (a) => {
    const uuid = chartOf(a), id = a.appointment_id;
    const ex = chartTabsRef.current.find((t) => t.kind === "chart" && t.uuid === uuid && t.errand?.telehealth && t.errand.appointment_id === id);
    if (!ex && !chartTabsRef.current.some((t) => t.uuid === uuid) && chartTabsRef.current.length >= MAX_TABS) { toast("You have 8 tabs open — close one to open another."); return; }
    try {
      const { data } = await api.post(`/api/edi/televisit/appointments/${id}/start`, {});
      const start = { ...data, _n: Date.now() };
      if (ex) {
        setChartTabs((cur) => cur.map((t) => (t.kind === "chart" && t.uuid === uuid && t.errand?.appointment_id === id ? { ...t, errand: { ...t.errand, start } } : t)));
        setActiveTab(uuid); setTimeout(() => window.dispatchEvent(new CustomEvent("revq:th-open-tele", { detail: id })), 50);
        window.dispatchEvent(new CustomEvent("revq:th-refresh")); return;
      }
      const errand = { appointment_id: id, telehealth: true, appt: a, start, n: Date.now() };
      setChartTabs((cur) => (cur.some((t) => t.kind === "chart" && t.uuid === uuid)
        ? cur.map((t) => (t.kind === "chart" && t.uuid === uuid ? { ...t, errand, fromLabel: "Telemedicine" } : t))
        : [...cur, { kind: "chart", uuid, name: patientName(a), fromLabel: "Telemedicine", intent: null, errand }]));
      setTimeout(() => setActiveTab(uuid), 0);
      window.dispatchEvent(new CustomEvent("revq:th-refresh"));
    } catch (e) { toast.error(teleStartError(e)); }
  };
  const noteReadyKey = (key, appt) => {
    setChartTabs((cur) => cur.map((x) => (keyOf(x) === key ? { ...x, dot: true } : x)));
    toast(`${patientName(appt)}'s note is ready to review`, { action: { label: "Open", onClick: () => { setActiveTab(key); setTimeout(() => window.dispatchEvent(new CustomEvent("revq:th-open-note", { detail: appt.appointment_id })), 50); } } });
  };
  const noteReady = (t) => {
    setChartTabs((cur) => cur.map((x) => (x.kind === "visit" && x.id === t.id ? { ...x, dot: true } : x)));
    toast(`${patientName(t.appt)}'s note is ready to review`, { action: { label: "Open", onClick: () => { focusVisit(t.id); setTimeout(() => window.dispatchEvent(new CustomEvent("revq:th-open-note", { detail: t.id })), 50); } } });
  };
  const renameTab = (key, name) => setChartTabs((cur) => cur.map((t) => (keyOf(t) === key ? { ...t, name } : t)));
  const closeAddPatient = () => {
    const t = chartTabs.find((x) => x.kind === "addPatient");
    const origin = t?.origin || "page";
    const originExists = origin === "page" || chartTabs.some((x) => keyOf(x) === origin);
    setChartTabs((cur) => cur.filter((x) => x.kind !== "addPatient"));
    setActiveTab(originExists ? origin : "page");
  };
  const closeTab = (key, force) => {
    const ct = chartTabsRef.current.find((t) => keyOf(t) === key);
    if (ct?.kind === "note" && ct.editing && !force) { setCloseAsk(ct); return; }
    if (ct && (ct.kind === "claim" || ct.origin)) {
      if (ct.kind === "claim" && ct.editing && !force) { setCloseAsk(ct); return; }
      const list = chartTabsRef.current, i = list.findIndex((t) => keyOf(t) === key);
      const back = ct.origin && ct.origin !== key && hasTab(ct.origin) ? ct.origin : i > 0 ? keyOf(list[i - 1]) : "page";
      setChartTabs((cur) => cur.filter((t) => keyOf(t) !== key));
      setActiveTab((a) => (a === key ? back : a));
      return;
    }
    if (key.startsWith("visit:") && liveRef.current?.id === key.slice(6)) { setChartTabs((cur) => cur.map((t) => (keyOf(t) === key ? { ...t, hidden: true } : t))); setActiveTab((a) => (a === key ? "page" : a)); return; }
    setChartTabs((cur) => cur.filter((t) => keyOf(t) !== key));
    setActiveTab((a) => (a === key ? "page" : a));
  };
  const closeNote = (saved, noteId) => {
    closeTab(`note:${noteId}`, true);
    setActiveTab("page");
    if (saved) window.dispatchEvent(new CustomEvent("revq:expand-encounter", { detail: { noteId } }));
  };

  // Any side-menu navigation returns to the first (page) tab; chart tabs stay open.
  useEffect(() => { setActiveTab("page"); setOverflowOpen(false); }, [location.pathname]);
  useEffect(() => { const h = () => { setActiveTab("page"); setOverflowOpen(false); }; window.addEventListener("revq:nav-page", h); return () => window.removeEventListener("revq:nav-page", h); }, []);
  // Sign-out clears open tabs.
  useEffect(() => { if (!token) { setChartTabs([]); try { sessionStorage.removeItem("revq.charttabs"); } catch { /* ignore */ } } }, [token]);


  // Background jobs: poll each until coded, then fire exactly one alert per document.
  useEffect(() => {
    if (!bgJobs.length) return undefined;
    let alive = true;
    const iv = setInterval(() => {
      bgJobs.forEach(async (job) => {
        try {
          const n = await getNote(job.id);
          if (!n || !isNoteDone(n) || !alive) return;
          setBgJobs((prev) => prev.filter((j) => j.id !== job.id));
          window.dispatchEvent(new CustomEvent("revq:jobs-changed", { detail: { id: job.id, done: true } }));
          const nm = n.patient_name || job.filename || "A document";
          const failed = isNoteFailed(n);
          toast[failed ? "error" : "success"](
            failed ? `${nm} — couldn’t be coded` : `${nm} — claim coded`,
            {
              description: failed ? "Open to see what happened" : "Ready — needs a look before it goes out",
              action: { label: "Open", onClick: () => setWatch({ id: job.id, filename: job.filename }) },
              duration: 9000,
            }
          );
        } catch { /* keep trying next tick */ }
      });
    }, 2500);
    return () => { alive = false; clearInterval(iv); };
  }, [bgJobs]);

  // Global "Upload" (Scan with Lens) lightbox — opens from anywhere via the topbar/sidebar CTA.
  // Context-aware: on /create-claims offer claim flows only (no remittances); on /payments offer
  // the remittance flow only (no provider / claim option). A pending kind (deep-link) wins.
  useEffect(() => {
    const openLens = (e) => {
      sessionStorage.removeItem("revq.pendingNewClaim"); // legacy flag — the route decides the mode now
      let kind = e?.detail?.kind; // an explicit kind from the topbar buttons wins
      if (!kind) {
        kind = "pdf";
        if (location.pathname === "/create-claims") kind = "note";
        else if (location.pathname === "/payments") kind = "remittance";
      }
      setLens({ open: true, kind });
    };
    window.addEventListener("revq:new-claim", openLens);
    return () => window.removeEventListener("revq:new-claim", openLens);
  }, [location.pathname]);

  // "Run in the background" from the coding workspace → hand the job to the bgJobs watcher.
  useEffect(() => {
    const onBg = (e) => {
      const job = e.detail;
      if (!job?.id) return;
      setBgJobs((prev) => (prev.some((j) => j.id === job.id) ? prev : [...prev, job]));
    };
    window.addEventListener("revq:background-job", onBg);
    return () => window.removeEventListener("revq:background-job", onBg);
  }, []);
  const embedded = ["/denials", "/rules", "/attachments", "/data-migration", "/modules", "/eligibility", "/authorizations", "/patient-billing", "/records/patients", "/records/patients-v2"];
  const fullBleed = ["/payments", "/home", "/create-claims", "/dictation", "/encounter-review", "/settings", "/patients"].includes(location.pathname) || location.pathname.startsWith("/payments-v2") || embedded.includes(location.pathname);

  // Any route change away from the workspace releases the encounter-collapse.
  useEffect(() => { if (location.pathname !== "/records/patients") setSidebarCollapsed(false); }, [location.pathname]);

  // Apply the saved global text-size (zoom) once the app shell mounts.
  useEffect(() => { applyTextScale(getTextScale()); }, []);

  if (bootstrapping) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="revq-spinner" style={{ borderTopColor: "#000", borderColor: "rgba(0,0,0,.18)" }} />
      </div>
    );
  }
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <ClientProvider>
      <ChromeContext.Provider value={{ setSidebarCollapsed }}>
        <ChartTabsContext.Provider value={{ openChart, openNote, openAddPatient, openNewVisit, openChartTab, openClaim, openClaimIds, openMpu, openEra }}>
        <div className="flex h-full w-full overflow-hidden">
          <style>{`.rq-sidenav{transition:width .3s cubic-bezier(.32,.72,0,1),min-width .3s cubic-bezier(.32,.72,0,1)}body.th-dragging .rq-sidenav{transition:none}.rq-navtgl:focus-visible{outline:2px solid #5856D6;outline-offset:2px}@media (prefers-reduced-motion:reduce){.rq-sidenav{transition:none}}`}</style>
          <Sidebar collapsed={navCollapsed} onToggle={sidebarCollapsed ? undefined : toggleNav} />
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <Topbar />
            <LongVisitBanner />
            {chartTabs.length > 0 ? (
              <div role="tablist" aria-label="Open tabs" onKeyDown={tabKeys} className="flex-none flex items-center bg-white relative z-[4]" style={{ height: "44px", padding: "0 24px", gap: "20px", borderBottom: "1px solid #E9E9EC", overflow: "visible" }} data-testid="chart-tab-strip">
                <style>{`
.cts-pagetab,.cts-tabbtn{position:relative;height:44px;display:inline-flex;align-items:center;gap:8px;font-size:13.5px;line-height:1;background:none;border:0;cursor:pointer;color:#6E6E6B;font-weight:400;min-width:0;transition:color .12s cubic-bezier(.4,0,.2,1)}
.cts-pagetab:hover,.cts-chip:hover .cts-tabbtn{color:#141412}
.cts-pagetab[data-active="true"],.cts-chip[data-active="true"] .cts-tabbtn{color:#141412;font-weight:600}
.cts-pagetab[data-active="true"]::after,.cts-chip[data-active="true"]::after{content:"";position:absolute;left:0;right:0;bottom:-1px;height:2px;background:#141412;border-radius:0}
.cts-pagetab:focus-visible,.cts-tabbtn:focus-visible,.cts-close:focus-visible,.cts-overflow:focus-visible{outline:2px solid #5856D6;outline-offset:2px;border-radius:2px}
.cts-chip{position:relative;height:44px;display:inline-flex;align-items:center;gap:8px;flex:0 1 auto;min-width:110px}
.cts-dos{color:#6E6E6B;font-weight:400;white-space:nowrap;flex:none}
.cts-label{display:inline-block;max-width:180px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cts-close{width:18px;height:18px;border-radius:4px;display:inline-flex;align-items:center;justify-content:center;color:#9B9B97;margin-left:-2px;flex:none;transition:background-color .12s,color .12s}
.cts-close:hover{background:#F1F1F4;color:#141412}
.cts-divider{width:1px;height:20px;background:#E9E9EC;flex:none}
.cts-overflow{height:26px;padding:0 8px;border:1px solid #E9E9EC;border-radius:6px;font-size:11.5px;font-weight:600;color:#6C6C69;background:#fff;display:inline-flex;align-items:center;flex:none;cursor:pointer;transition:background-color .12s}
.cts-overflow:hover{background:#F4F4F9}
@keyframes cts-tab-in{0%{opacity:0;transform:translateY(6px);background:#F1F1F4}45%{opacity:1;transform:translateY(0)}100%{opacity:1;transform:translateY(0);background:transparent}}
.cts-chip-enter{animation:cts-tab-in 1.2s cubic-bezier(.32,.72,0,1);border-radius:8px}
@media (prefers-reduced-motion:reduce){.cts-chip-enter{animation:none}}
`}</style>
                <button role="tab" aria-selected={activeTab === "page"} data-active={activeTab === "page" ? "true" : "false"} onClick={() => setActiveTab("page")} data-testid="chart-tab-page" className="cts-pagetab">{pageLabel}</button>
                <span className="cts-divider" />
                {(() => {
                  const VISIBLE = 8;
                  const shown = chartTabs.filter((t) => !t.hidden);
                  let visible = shown, overflow = [];
                  if (shown.length > VISIBLE) {
                    visible = shown.slice(0, VISIBLE); overflow = shown.slice(VISIBLE);
                    if (overflow.some((t) => keyOf(t) === activeTab)) {
                      const act = overflow.find((t) => keyOf(t) === activeTab);
                      overflow = [visible[VISIBLE - 1], ...overflow.filter((t) => keyOf(t) !== activeTab)];
                      visible = [...visible.slice(0, VISIBLE - 1), act];
                    }
                  }
                  const label = (t) => (t.kind === "claim" ? claimName(t) : t.kind === "note" ? `Note · ${t.name}` : t.kind === "addPatient" ? "New patient" : t.kind === "mpu" ? "Multi-part uploads" : t.kind === "era" ? t.label : t.name);
                  const isNv = (t) => t.kind === "newVisit";
                  const TabChip = (t) => {
                    const k = keyOf(t); const isNote = t.kind === "note"; const isAdd = t.kind === "addPatient" || isNv(t);
                    const isClaim = t.kind === "claim";
                    const Ic = isClaim ? DocIcon : isNote ? PencilIcon : t.kind === "visit" ? VideoIcon : t.kind === "mpu" ? UploadIcon : t.kind === "era" ? DocIcon : isAdd ? PlusIcon : UserIcon;
                    const active = activeTab === k, dos = isClaim ? claimDos(t) : "";
                    return (
                      <div key={k} role="presentation" data-active={active ? "true" : "false"} data-testid={`chart-tab-${k}`} className={`cts-chip ${k === newTabKey ? "cts-chip-enter" : ""}`} onAnimationEnd={() => { if (k === newTabKey) setNewTabKey(null); }}>
                        <button role="tab" aria-selected={active} onClick={() => setActiveTab(k)} className="cts-tabbtn" title={isClaim ? `Claim · ${label(t)}${dos ? ` · ${dos}` : ""}` : label(t)}>
                          <Ic size={15} strokeWidth={1.75} className="flex-none" /> <span className="cts-label">{label(t)}</span>{dos ? <span className="cts-dos" style={{ marginLeft: -4 }}>· {dos}</span> : null}{t.dot ? <span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: "#5856D6" }} data-testid={`chart-tab-dot-${k}`} /> : null}
                          {isClaim && t.editing ? <span className="w-[7px] h-[7px] rounded-full flex-none" style={{ background: "#FF9500" }} title="Unsaved note edits" data-testid={`chart-tab-edit-dot-${k}`} /> : null}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); t.kind === "addPatient" ? closeAddPatient() : closeTab(k); }} aria-label={isClaim ? `Close ${label(t)} claim` : `Close ${label(t)}`} data-testid={`chart-tab-close-${k}`} className="cts-close"><XIcon size={12} /></button>
                      </div>
                    );
                  };
                  return (
                    <>
                      {visible.map(TabChip)}
                      {overflow.length ? (
                        <div className="relative" style={{ flex: "none" }}>
                          <button onClick={() => setOverflowOpen((v) => !v)} data-testid="chart-tab-overflow" className="cts-overflow">+{overflow.length}</button>
                          {overflowOpen ? (
                            <div className="absolute left-0 top-[34px] z-[20] rounded-[10px] p-1 min-w-[220px]" style={{ background: "#fff", border: "1px solid #E9E9EC", boxShadow: "0 12px 30px rgba(20,20,18,.16)" }} data-testid="chart-tab-overflow-menu">
                              {overflow.map((t) => {
                                const k = keyOf(t); const isNote = t.kind === "note"; const Ic = t.kind === "claim" ? DocIcon : isNote ? PencilIcon : UserIcon;
                                return (
                                  <div key={k} className="flex items-center gap-2 px-2 py-1.5 rounded-[7px] hover:bg-black/[.04]">
                                    <button onClick={() => { setActiveTab(k); setOverflowOpen(false); }} data-testid={`chart-tab-overflow-item-${k}`} className="flex items-center gap-1.5 text-[12.5px] min-w-0 flex-1" style={{ color: "#141412" }}><Ic size={15} strokeWidth={1.75} className="flex-none" /><span className="truncate">{label(t)}</span></button>
                                    <button onClick={() => closeTab(k)} aria-label={`Close ${t.name}`} data-testid={`chart-tab-overflow-close-${k}`} className="cts-close"><XIcon size={12} /></button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            ) : null}
            <div className="flex-1 relative overflow-hidden min-w-0">
              <div className={`absolute inset-0 overflow-y-auto ${fullBleed ? "px-0 py-0" : "px-7 py-6"}`} style={{ display: activeTab === "page" ? "block" : "none" }}>
                <Outlet />
              </div>
              {chartTabs.map((t) => {
                const k = keyOf(t);
                return (
                  <div key={k} className={`absolute inset-0 bg-white ${t.kind === "claim" ? "overflow-hidden" : "overflow-y-auto"}`} style={{ display: activeTab === k ? "block" : "none" }} data-testid={`chart-pane-${k}`}>
                    {t.kind === "claim" ? (
                      <ClaimWorkspace embedded active={activeTab === k} tabKey={k} items={t.items} index={Math.min(t.index, t.items.length - 1)} onIndex={(i) => claimIndex(k, i)}
                        backLabel={t.backLabel} onClose={() => claimBack(t)} onChanged={() => window.dispatchEvent(new CustomEvent("revq:claims-changed"))} onEditing={(v) => setClaimEditing(k, v)} initialRail={t.railTab} />
                    ) : t.kind === "note" ? (
                      <NoteEditor noteId={t.noteId} chartUuid={t.chartUuid} fromLabel={t.fromLabel} onClose={closeNote} onEditing={(v) => setClaimEditing(k, v)} />
                    ) : t.kind === "visit" ? (
                      <VisitScreen appt={t.appt} start={t.start} active={activeTab === k} onNoteReady={() => noteReady(t)}
                        onBack={(stay) => { setActiveTab("page"); if (!stay) navigate("/telehealth-v2"); }} />
                    ) : t.kind === "newVisit" ? (
                      <NewVisitTab prefill={t.prefill} onBack={() => setActiveTab("page")} onRename={(n) => renameTab(k, n)} onClose={() => closeTab(k)}
                        onBooked={(a) => { closeTab(k); setActiveTab("page"); window.dispatchEvent(new CustomEvent("revq:th-booked", { detail: a })); }} />
                    ) : t.kind === "mpu" ? (
                      <MultiPartUploadsTab initialBatchId={t.batchId} backLabel={t.backLabel}
                        onBack={() => setActiveTab(t.origin && chartTabs.some((x) => keyOf(x) === t.origin) ? t.origin : "page")}
                        onOpenVisit={(noteId) => { try { sessionStorage.setItem("revq.er.openVisit", noteId); } catch { /* ignore */ } setActiveTab("page"); navigate("/encounter-review"); }}
                        onFind={(seg) => { try { sessionStorage.setItem("revq.er.search", seg.patient_label || ""); } catch { /* ignore */ } setActiveTab("page"); navigate("/encounter-review"); }} />
                    ) : t.kind === "era" ? (
                      <EraWorkspaceV2 fileId={t.fileId} onExit={() => closeTab(k)} onStep={(n) => setChartTabs((cur) => cur.map((x) => (x.uuid === t.uuid ? { ...x, fileId: n } : x)))} />
                    ) : t.kind === "addPatient" ? (
                      <AddPatientPage
                        prefill={t.prefill}
                        focus={t.focus}
                        originLabel={t.originLabel}
                        onBack={() => setActiveTab(t.origin)}
                        onClose={closeAddPatient}
                        onCreated={(name, chartUuid) => {
                          toast(`Added ${name}`, { style: { background: "#141412", color: "#fff", borderRadius: "12px" } });
                          closeAddPatient();
                          if (chartUuid) openChart({ uuid: chartUuid, name });
                        }}
                      />
                    ) : (
                      <div className={t.origin ? "h-full flex flex-col" : undefined}>
                        <div className={t.origin ? "flex-none px-5 py-2 border-b border-[#E9E9EC] bg-white" : "px-5 pt-3"}>
                          <button onClick={() => setActiveTab(t.origin && chartTabs.some((x) => keyOf(x) === t.origin) ? t.origin : "page")} className="text-[12px] font-semibold inline-flex items-center gap-1" style={{ color: "#5856D6" }} data-testid={`chart-back-${t.uuid}`}><ChevronLeft size={13} /> {t.fromLabel || "Back"}</button>
                        </div>
                        <div className={t.origin ? "relative flex-1 min-h-0" : undefined}>
                        <ChartV2 embedded uuid={t.uuid} errand={t.errand} active={activeTab === k} onTeleBack={() => { setActiveTab("page"); navigate("/telehealth-v2"); }} onTeleNoteReady={() => noteReadyKey(k, t.errand?.appt || {})} {...(t.intent === "dictate" ? { autoEncounter: true, targetTab: { tab: "focus" } } : t.intent === "documents" ? { targetTab: { tab: "engagement-docs", n: t.intentN } } : {})} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {closeAsk ? (
            <div className="fixed inset-0 z-[9800] flex items-center justify-center" style={{ background: "rgba(20,20,18,.32)" }} onClick={() => setCloseAsk(null)} data-testid="claim-close-confirm">
              <div role="alertdialog" aria-labelledby="cc-close-t" className="bg-white rounded-[14px] p-5 w-[min(420px,92vw)]" style={{ boxShadow: "0 24px 60px rgba(20,20,18,.28)" }} onClick={(e) => e.stopPropagation()}>
                <div id="cc-close-t" className="text-[15px] font-semibold" style={{ color: "#141412" }}>Close with unsaved edits?</div>
                <p className="text-[13px] mt-1.5 leading-[1.5]" style={{ color: "#6C6C69" }}>{closeAsk.kind === "note" ? `Your edits to ${closeAsk.name || "this patient"}'s note aren't saved, or chart items are staged but not added. Closing discards them.` : `The note on ${claimName(closeAsk)}'s claim is still being edited. Closing discards those edits.`}</p>
                <div className="flex justify-end gap-2 mt-4">
                  <button autoFocus onClick={() => setCloseAsk(null)} className="h-9 px-3.5 rounded-[8px] border text-[13px] font-semibold bg-white hover:bg-[#F4F4F9]" style={{ borderColor: "#E9E9EC", color: "#141412" }} data-testid="claim-close-keep">Keep editing</button>
                  <button onClick={() => { const k = keyOf(closeAsk); setCloseAsk(null); closeTab(k, true); }} className="h-9 px-3.5 rounded-[8px] text-[13px] font-semibold text-white hover:opacity-90" style={{ background: "#141412" }} data-testid="claim-close-go">Close tab</button>
                </div>
              </div>
            </div>
          ) : null}
          <ExperienceMode />
          {!watch ? <AskRevqDock /> : null}
          <NpiBootstrapModal />
          <AlertsNudge />
          <ClientPickerOverlay />
          <LensUploadModal
            open={lens.open}
            kind={lens.kind}
            onClose={() => setLens((l) => ({ ...l, open: false }))}
            onDone={() => { setLens((l) => ({ ...l, open: false })); navigate("/create-claims"); }}
            onWatchClaim={(job) => { setWatch(job); window.dispatchEvent(new CustomEvent("revq:jobs-changed", { detail: job })); }}
            onBackgroundJob={(job) => { setBgJobs((prev) => (prev.some((j) => j.id === job.id) ? prev : [...prev, job])); window.dispatchEvent(new CustomEvent("revq:jobs-changed", { detail: job })); }}
          />
          {watch ? (
            <ClaimWorkspace
              items={[{ id: watch.id }]} index={0} onIndex={() => {}}
              backLabel="upload" onClose={() => setWatch(null)} onChanged={() => {}}
            />
          ) : null}
        </div>
          {live && !(activeTab === (live.tabKey || `visit:${live.id}`) && live.onTele !== false) ? <AwayBar live={live} onReturn={() => { const k = live.tabKey || `visit:${live.id}`; if (k.startsWith("visit:")) focusVisit(live.id); else setActiveTab(k); setTimeout(() => window.dispatchEvent(new CustomEvent("revq:th-open-tele", { detail: live.id })), 50); }} /> : null}
        </ChartTabsContext.Provider>
      </ChromeContext.Provider>
    </ClientProvider>
  );
}
